import React from "react";
import { useLang } from "@/lib/i18n";
import { Gift, CalendarDays, Clock, Trophy, Sparkles, ShieldCheck } from "lucide-react";

const RESULT_STYLE = {
  WON: { bg: "#14532D", color: "#4ADE80", border: "rgba(34,197,94,0.45)", dot: "#4ADE80" },
  LOST: { bg: "#4A0512", color: "#F87171", border: "rgba(239,68,68,0.5)", dot: "#F87171" },
  PENDING: { bg: "#451A03", color: "#FBBF24", border: "rgba(245,158,11,0.45)", dot: "#FBBF24" },
  VOID: { bg: "rgba(255,255,255,0.1)", color: "rgba(255,255,255,0.75)", border: "rgba(255,255,255,0.25)", dot: "#ccc" },
};

function ResultBadge({ result, label }) {
  const s = RESULT_STYLE[result] || RESULT_STYLE.PENDING;
  return (
    <span className="inline-flex items-center gap-1.5 rounded-full text-[11px] font-bold font-body px-2.5 py-1 whitespace-nowrap border" style={{ background: s.bg, color: s.color, borderColor: s.border }}>
      <span className="w-1.5 h-1.5 rounded-full" style={{ background: s.dot }} />{label}
    </span>
  );
}

function PrevCard({ tip, results, labels, idx }) {
  return (
    <div data-testid={`free-tip-prev-${idx}`} className="at-card rounded-2xl p-5">
      <div className="flex items-center justify-between gap-2">
        <span className="text-[11px] font-body text-white/55 tabular-nums inline-flex items-center gap-1.5"><CalendarDays size={13} /> {tip.date}{tip.kickoff ? ` · ${tip.kickoff}` : ""}</span>
        <ResultBadge result={tip.result} label={results[tip.result] || tip.result} />
      </div>
      {tip.league && <div className="mt-2 text-[11px] font-heading font-semibold uppercase tracking-wide text-[var(--trust-gold)]/80">{tip.league}</div>}
      <div className="mt-1 font-heading font-bold text-white leading-snug">{tip.match}</div>
      <div className="mt-3 pt-3 border-t border-[var(--border-light)] flex items-end justify-between">
        <div>
          <div className="text-[10px] uppercase tracking-wide text-white/50 font-heading">{labels.tip}</div>
          <div className="font-body text-sm font-semibold text-[var(--trust-gold)]">{tip.tip}</div>
        </div>
        <div className="text-right">
          <div className="text-[10px] uppercase tracking-wide text-white/50 font-heading">{labels.odd}</div>
          <div className="font-serif-display font-bold text-xl text-white tabular-nums">{tip.odd != null ? Number(tip.odd).toFixed(2) : "—"}</div>
        </div>
      </div>
    </div>
  );
}

export default function FreeDailyTip({ tips = [] }) {
  const { t } = useLang();
  const results = t("freeTip.results");
  const labels = { tip: t("freeTip.tipLabel"), odd: t("freeTip.oddLabel") };
  const featured = tips[0];
  const previous = tips.slice(1);

  return (
    <section id="free-tip" className="py-20 lg:py-28" style={{ background: "var(--at-deep)" }}>
      <div className="max-w-7xl mx-auto px-5 sm:px-8">
        <div className="max-w-2xl mb-10">
          <div className="flex items-center gap-3 mb-4">
            <span className="font-heading text-xs font-extrabold tracking-[0.25em] uppercase gold-gradient-text">05</span>
            <span className="w-8 h-px bg-[var(--trust-gold)]/50" />
            <span className="font-heading text-[11px] font-bold tracking-[0.2em] uppercase text-white/60 inline-flex items-center gap-1.5"><Gift size={13} /> {t("freeTip.kicker")}</span>
          </div>
          <h2 className="font-serif-display font-bold tracking-tight text-white text-3xl sm:text-4xl lg:text-5xl leading-tight">{t("freeTip.title")}</h2>
          <p className="mt-4 text-base font-body text-white/75 leading-relaxed">{t("freeTip.subtitle")}</p>
        </div>

        {!featured ? (
          <div data-testid="free-tip-empty" className="at-card rounded-3xl p-12 text-center">
            <Gift size={34} className="mx-auto text-white/40" />
            <p className="mt-4 font-body text-white/60 text-sm">{t("freeTip.empty")}</p>
          </div>
        ) : (
          <>
            <div data-testid="free-tip-featured" className="relative overflow-hidden rounded-3xl animate-fade-up border-2 border-[var(--trust-gold)]" style={{ background: "linear-gradient(160deg,#5B0715,#38040C)", boxShadow: "0 0 40px rgba(255,215,0,0.15)" }}>
              <div className="h-1.5 austria-stripe" />
              <div className="p-6 sm:p-9 grid lg:grid-cols-[1.4fr_1fr] gap-8">
                <div>
                  <div className="flex flex-wrap items-center gap-3 mb-5">
                    <span className="inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-[11px] font-extrabold font-heading tracking-wide text-[#38040C]" style={{ background: "linear-gradient(135deg,#FFE566,#FFD700)" }}><Sparkles size={13} /> {t("freeTip.badge")}</span>
                    <span className="text-xs font-heading font-semibold uppercase tracking-wide text-white/55">{t("freeTip.today")}</span>
                    <span className="ml-auto"><ResultBadge result={featured.result} label={results[featured.result] || featured.result} /></span>
                  </div>
                  <div className="flex flex-wrap items-center gap-x-5 gap-y-2 text-sm font-body text-white/70 mb-3">
                    {featured.date && <span className="inline-flex items-center gap-1.5"><CalendarDays size={15} className="text-white/50" /> {featured.date}</span>}
                    {featured.kickoff && <span className="inline-flex items-center gap-1.5"><Clock size={15} className="text-white/50" /> {featured.kickoff}</span>}
                    {featured.league && <span className="inline-flex items-center gap-1.5 font-semibold text-white/85"><Trophy size={15} className="text-[var(--trust-gold)]" /> {featured.league}</span>}
                  </div>
                  <h3 className="font-serif-display font-bold text-white text-2xl sm:text-3xl leading-tight tracking-tight">{featured.match}</h3>
                  {featured.analysis ? (
                    <div className="mt-5 rounded-2xl bg-white/5 border border-[var(--border-light)] p-4">
                      <div className="text-[11px] font-bold font-heading uppercase tracking-wide text-white/55 mb-1.5 inline-flex items-center gap-1.5"><ShieldCheck size={13} className="text-[var(--trust-gold)]" /> {t("freeTip.analysisLabel")}</div>
                      <p className="font-body text-sm text-white/80 leading-relaxed">{featured.analysis}</p>
                    </div>
                  ) : null}
                </div>
                <div className="rounded-2xl p-6 flex flex-col justify-center" style={{ background: "var(--at-darkest)", border: "1px solid var(--border-gold)" }}>
                  <div className="text-[11px] font-bold font-heading uppercase tracking-[0.18em] text-white/50 mb-1.5">{t("freeTip.tipLabel")}</div>
                  <div data-testid="free-tip-pick" className="font-serif-display font-bold text-2xl sm:text-3xl leading-tight text-white">{featured.tip}</div>
                  <div className="mt-5 pt-5 border-t border-white/10 flex items-end justify-between">
                    <span className="text-[11px] font-bold font-heading uppercase tracking-[0.18em] text-white/50">{t("freeTip.oddLabel")}</span>
                    <span className="font-serif-display font-bold text-4xl tabular-nums gold-gradient-text">{featured.odd != null ? Number(featured.odd).toFixed(2) : "—"}</span>
                  </div>
                </div>
              </div>
            </div>

            {previous.length > 0 && (
              <div className="mt-10" data-testid="free-tip-previous-list">
                <div className="text-xs font-heading font-bold uppercase tracking-[0.2em] text-white/55 mb-4">{t("freeTip.previousTitle")}</div>
                <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                  {previous.map((tip, i) => <PrevCard key={tip.id || i} tip={tip} results={results} labels={labels} idx={i} />)}
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </section>
  );
}
